/**
 * Standardized loading spinner component for consistent loading states across the platform
 */

import React from 'react';

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  message?: string;
  showMessage?: boolean;
  className?: string;
  centered?: boolean;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

const messageSizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  message = 'Loading...',
  showMessage = true,
  className = '',
  centered = true,
}) => {
  const spinner = (
    <div className={`animate-spin rounded-full border-b-2 border-primary-600 ${sizeClasses[size]}`} />
  );

  const content = (
    <div className={`flex items-center ${showMessage ? 'space-x-3' : ''} ${className}`}>
      {spinner}
      {showMessage && (
        <span className={`text-gray-600 ${messageSizeClasses[size]}`}>
          {message}
        </span>
      )}
    </div>
  );

  if (centered) {
    return (
      <div className="flex items-center justify-center py-8">
        {content}
      </div>
    );
  }

  return content;
};

// Convenience components for common loading scenarios
export const PageLoading: React.FC<{ message?: string }> = ({ 
  message = 'Loading page...' 
}) => (
  <div className="flex items-center justify-center py-12">
    <LoadingSpinner size="lg" message={message} />
  </div>
);

export const SectionLoading: React.FC<{ message?: string }> = ({ 
  message = 'Loading...' 
}) => (
  <div className="flex items-center justify-center py-6">
    <LoadingSpinner size="md" message={message} />
  </div>
);

export const InlineLoading: React.FC<{ message?: string }> = ({ 
  message = 'Loading...' 
}) => (
  <LoadingSpinner size="sm" message={message} centered={false} />
);